import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { apiClient } from '../api/client'
import { LoadingSpinner } from '../components/common'

interface AuthorizationContextValue {
  roles: string[]
  permissions: string[]
  hasPermission: (permissionKey: string) => boolean
  isAdmin: boolean
  isLoading: boolean
  refreshPermissions: () => Promise<void>
}

const AuthorizationContext = createContext<AuthorizationContextValue | undefined>(undefined)

interface PermissionsResponse {
  roles: string[]
  permissions: string[]
}

export const AuthorizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [roles, setRoles] = useState<string[]>([])
  const [permissions, setPermissions] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await apiClient.get<PermissionsResponse>('/api/permissions/me')
      setRoles(response.data.roles)
      setPermissions(response.data.permissions)
    } catch (error) {
      console.error('Failed to fetch permissions', error)
      setRoles([])
      setPermissions([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPermissions()
  }, [fetchPermissions])

  const hasPermission = useCallback(
    (permissionKey: string) => permissions.includes(permissionKey),
    [permissions],
  )

  const isAdmin = roles.includes('BO.Admin')

  if (isLoading) {
    return <LoadingSpinner fullPage />
  }

  return (
    <AuthorizationContext.Provider
      value={{ roles, permissions, hasPermission, isAdmin, isLoading, refreshPermissions: fetchPermissions }}
    >
      {children}
    </AuthorizationContext.Provider>
  )
}

export const useAuthorization = (): AuthorizationContextValue => {
  const context = useContext(AuthorizationContext)
  if (!context) {
    throw new Error('useAuthorization must be used within an AuthorizationProvider')
  }
  return context
}
